import EditContentBankResourcesDialog from 'components/CreateAssignment/EditContentBankResourcesDialog';
import DebugDialog from 'components/ViewAssignment/DebugDialog';
import InstructorUploadSubmissionsDialog from 'components/ViewAssignment/InstructorUploadSubmissionsDialog';
import ViewBaselineDialog from 'components/ViewAssignment/ViewBaselineDialog';
import ViewGradingSettingsDialog from 'components/ViewAssignment/ViewGradingSettingsDialog';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import ErrorPage from 'pages/Error/404';
import { PageContext } from 'pages/InstructorViewAssignment/PageProvider';
import Desktop from 'pages/InstructorViewAssignment/desktop';
import Mobile from 'pages/InstructorViewAssignment/mobile';
import Tablet from 'pages/InstructorViewAssignment/tablet';
import { useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import { Button, Loading, Stack, Text } from 'shell/components';
import { getDuplicateTitle, isAssignmentValid } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageLayout = () => {
    const { auth } = useAuth();
    const { isLoading, course, assignments, setAssignments, currentAssignmentId, submissions } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const params = useParams();
    const { message } = useSnackbar();
    const { device } = useViewport();

    const {
        assignment,
        viewBaselineDialog,
        setViewBaselineDialog,
        viewContentBankResourcesDialog,
        setViewContentBankResourcesDialog,
        viewGradingSettingsDialog,
        setViewGradingSettingsDialog,
        uploadAssignmentsDialog,
        setUploadAssignmentsDialog,
        debugDialog,
        setDebugDialog,
        createSubmissions,
        exportSubmissions,
        resetSubmissions,
    } = useContext(PageContext);

    const handleClickBack = () => {
        history.push('/assignments');
    };

    const handleClickDuplicate = async (assignment) => {
        const timestamp = new Date().getTime();
        const duplicate = {
            ...omit(assignment, ['_id', 'is_rag_ready']),
            id: uuid(),
            owner_account_id: auth.id,
            created_on: timestamp,
            modified_on: timestamp,
            title: getDuplicateTitle(assignment.title),
            is_draft: true,
        };
        setAssignments((current) => [...current, duplicate]);
        message.success(
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                <Text color={'inherit'} variant={'p1'}>
                    Your assignment was successfully duplicated.
                </Text>
                <Button
                    color={'neutral'}
                    size={'large'}
                    variant={'borderless'}
                    onClick={() => history.push(`/assignments/${duplicate.id}`)}
                >
                    Open
                </Button>
            </Stack>
        );
        await courseService.createCourseAssignment(course.id, duplicate);
    };

    const handleClickUnpublish = (assignment) => {
        setDialog({
            open: true,
            message: 'Unpublish assignment?',
            description: 'Are you sure you want to unpublish this assignment?',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Unpublish',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: true,
                        };
                    });
                });
                closeDialog();
                message.success('Your assignment was successfully updated.');
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: true,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickPublish = (assignment) => {
        if (!isAssignmentValid(assignment)) {
            return setDialog({
                open: true,
                message: 'Required Information Incomplete',
                description:
                    'Before you can publish this assignment, please edit it and add any missing required information.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Ok',
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        setDialog({
            open: true,
            message: 'Publish assignment?',
            description: 'Are you sure you want to publish this assignment?',
            note: '',
            variant: 'primary',
            actionButtonText: 'Publish',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                const timestamp = new Date().getTime();
                setAssignments((current) => {
                    return current.map((currentAssignment) => {
                        if (currentAssignment.id !== assignment.id) return currentAssignment;
                        return {
                            ...currentAssignment,
                            modified_on: timestamp,
                            is_draft: false,
                        };
                    });
                });
                closeDialog();
                message.success('Your assignment was successfully updated.');
                await assignmentService.updateAssignment(assignment.id, {
                    ...omit(assignment, ['_id']),
                    modified_on: timestamp,
                    is_draft: false,
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickEdit = () => {
        history.push(`/assignments/${assignment.id}/edit`);
    };

    const handleClickViewBaseline = () => {
        setViewBaselineDialog({ open: true });
    };

    const handleClickViewContentBank = () => {
        setViewContentBankResourcesDialog({ open: true });
    };

    const handleClickViewGradingSettings = () => {
        setViewGradingSettingsDialog({ open: true });
    };

    const handleClickViewSubmissions = () => {
        history.push(`/assignments/${assignment.id}/submissions`);
    };

    const handleClickUploadSubmissions = () => {
        setUploadAssignmentsDialog({ open: true });
    };

    const handleClickShowDebugInfo = () => {
        setDebugDialog({ open: true });
    };

    const handleSelect = ({ key }) => {
        if (key === 'csv') {
            exportSubmissions();
        }
        if (key === 'delete') {
            resetSubmissions();
        }
    };

    const props = {
        assignment,
        submissions,
        onClickBack: handleClickBack,
        onClickDuplicate: handleClickDuplicate,
        onClickUnpublish: handleClickUnpublish,
        onClickPublish: handleClickPublish,
        onClickEdit: handleClickEdit,
        onClickViewBaseline: handleClickViewBaseline,
        onClickViewContentBank: handleClickViewContentBank,
        onClickViewGradingSettings: handleClickViewGradingSettings,
        onClickViewSubmissions: handleClickViewSubmissions,
        onClickUploadSubmissions: handleClickUploadSubmissions,
        onClickShowDebugInfo: handleClickShowDebugInfo,
        onSelect: handleSelect,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    if (isLoading || currentAssignmentId !== params.id) return <Loading />;

    if (assignments && !assignment) return <ErrorPage />;

    return (
        <>
            {renderResponsiveLayout(device)}
            {assignment && (
                <>
                    <ViewBaselineDialog
                        open={viewBaselineDialog.open}
                        rubric={assignment.rubric}
                        baseline={assignment.baseline}
                        onClose={() => setViewBaselineDialog({ open: false })}
                    />
                    <EditContentBankResourcesDialog
                        readOnly
                        open={viewContentBankResourcesDialog.open}
                        resources={assignment.content_bank_resources || []}
                        onClose={() => setViewContentBankResourcesDialog({ open: false })}
                        onSave={() => {}}
                    />
                    <ViewGradingSettingsDialog
                        open={viewGradingSettingsDialog.open}
                        onClose={() => setViewGradingSettingsDialog({ open: false })}
                    />
                </>
            )}
            <InstructorUploadSubmissionsDialog
                open={uploadAssignmentsDialog.open}
                assignment={assignment}
                onClose={() => setUploadAssignmentsDialog({ open: false })}
                onClick={createSubmissions}
            />
            <DebugDialog open={debugDialog.open} onClose={() => setDebugDialog({ open: false })} />
        </>
    );
};

export default PageLayout;
